import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { Loader } from "components/Loader";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  leavePalette,
  typeMeta,
  statusMeta,
} from "../MyZoneApply/Leave/leaveHelpers";
import { LeaveHistoryTable } from "../MyZoneApply/Leave/LeaveHistoryTable";
import { PermissionsDetails } from "./permissionDetails";
import {
  parseDate,
  useLeaveDetailsData,
  toApiDate,
} from "./useLeaveDetailsData";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";

const panelCardSx = {
  border: `1px solid ${leavePalette.border}`,
  borderRadius: 2,
  bgcolor: leavePalette.card,
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
};

const formatValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return Number.isInteger(number) ? `${number}` : `${number}`;
};

const formatShortDate = (value) => {
  const parsed = parseDate(value);
  return parsed ? format(parsed, "dd MMM yyyy") : value || "-";
};

const formatYearLabel = (year) => {
  if (!year) return "Select leave year";
  const range = `${formatShortDate(year.start_date)} - ${formatShortDate(
    year.end_date,
  )}`;
  return year.name ? `${year.name} (${range})` : range;
};

const formatHistoryDays = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${formatValue(number)} ${number <= 1 ? "Day" : "Days"}`;
};

const formatSummaryDays = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0 days";
  return `${formatValue(number)} ${number === 1 ? "day" : "days"}`;
};

const ProfileCard = ({ leaveCount, userDetails, displayName }) => {
  const employeeCode =
    leaveCount?.employee_id || userDetails?.employee_id || "-";
  const department =
    leaveCount?.department_name || userDetails?.department_name || "-";
  const designation =
    leaveCount?.designation || userDetails?.designation || "-";

  return (
    <Box sx={{ mb: 2 }}>
      <RequestDetailHeader
        status="Active"
        userDetails={{
          image_url: leaveCount?.image_url || userDetails?.image_url,
          user_name: displayName,
          employee_id: employeeCode,
          department_name: department,
          designation,
        }}
      />
    </Box>
  );
};

const YearSelector = ({
  selectedYear,
  selectedYearLabel,
  setSelectedYear,
  setTab,
  yearOptions,
}) => (
  <Select
    fullWidth
    size="small"
    displayEmpty
    value={selectedYearLabel || ""}
    onChange={(event) => {
      const year = yearOptions.find((option) => option.label === event.target.value);
      if (!year) return;
      setSelectedYear({
        id: year.id,
        name: year.name,
        start_date: year.start_date,
        end_date: year.end_date,
      });
      setTab(0);
    }}
    renderValue={() => (
      <Stack direction="row" spacing={1.25} alignItems="center">
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: leavePalette.subText }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: leavePalette.subText,
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            Year
          </Typography>
          <Typography
            sx={{
              color: leavePalette.text,
              fontSize: 12,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatYearLabel(selectedYear)}
          </Typography>
        </Box>
      </Stack>
    )}
    MenuProps={{
      PaperProps: {
        sx: {
          mt: 0.5,
          maxHeight: 260,
          borderRadius: 2,
          border: `1px solid ${leavePalette.border}`,
        },
      },
    }}
    sx={{
      mb: 2,
      height: 54,
      borderRadius: 1.5,
      bgcolor: leavePalette.card,
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        py: 0.75,
        px: 1.25,
      },
      "& fieldset": { borderColor: leavePalette.border },
      "&:hover fieldset": { borderColor: "#D7DCE5" },
      "&.Mui-focused fieldset": { borderColor: leavePalette.green },
    }}
  >
    {yearOptions.map((year) => (
      <MenuItem key={year.label} value={year.label}>
        {formatYearLabel(year)}
      </MenuItem>
    ))}
  </Select>
);

const BalanceCard = ({ leaveType, index }) => {
  const meta = typeMeta(leaveType.leave_type);
  const Icon = meta.icon;
  const entitled = Number(leaveType.credited_leave) || 0;
  const used = Number(leaveType.used_leave) || 0;
  const available = Math.max(entitled - used, 0);
  const availablePct = entitled
    ? Math.max(0, Math.min((available / entitled) * 100, 100))
    : 0;

  return (
    <Box
      key={leaveType.leave_type || index}
      sx={{
        p: 1.4,
        minHeight: 95,
        borderRadius: 2,
        bgcolor: `${meta.color}0F`,
        border: `1px solid ${meta.color}2E`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${meta.color}14`,
            color: meta.color,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            title={leaveType.leave_type}
            sx={{
              color: meta.color,
              display: "block",
              fontSize: 11,
              fontWeight: 800,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {leaveType.leave_type}
          </Typography>
          <Typography
            sx={{
              color: leavePalette.text,
              fontSize: 21,
              fontWeight: 800,
              lineHeight: 1.05,
              mt: 0.2,
            }}
          >
            {formatValue(available)}
          </Typography>
          <Typography sx={{ color: leavePalette.subText, fontSize: 11 }}>
            of {formatValue(entitled)}
          </Typography>
        </Box>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={availablePct}
        sx={{
          mt: 1.15,
          height: 4.5,
          borderRadius: 5,
          bgcolor: `${meta.color}14`,
          "& .MuiLinearProgress-bar": {
            bgcolor: meta.color,
            borderRadius: 5,
          },
        }}
      />
    </Box>
  );
};

const StatRow = ({ icon: Icon, color, label, value, valueColor, sub, isLast }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 0.8,
      borderBottom: isLast ? "none" : `1px solid ${leavePalette.border}`,
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.35,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}18`,
          color,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        sx={{
          color: leavePalette.subText,
          fontSize: 12,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1.5 }}>
      <Typography
        sx={{
          color: valueColor || leavePalette.text,
          fontSize: 13.5,
          fontWeight: 800,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ color: leavePalette.amber, fontSize: 10.5, mt: 0.15 }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

const RecentHistoryRow = ({ row, isLast }) => {
  const meta = typeMeta(row.leave_type);
  const Icon = meta.icon;
  const color = statusMeta(row.status, row.color_code);
  const dateLine = `${formatShortDate(row.from_date)} (${formatHistoryDays(
    row.business_days,
  )})`;

  return (
    <Stack
      direction="row"
      spacing={1.15}
      alignItems="center"
      sx={{
        py: 0.75,
        borderBottom: isLast ? "none" : `1px solid ${leavePalette.border}`,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.35,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${meta.color}16`,
          color: meta.color,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          title={row.leave_type}
          sx={{
            color: leavePalette.text,
          fontSize: 12,
            fontWeight: 800,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.leave_type || "-"}
        </Typography>
        <Typography
          title={dateLine}
          sx={{
            color: leavePalette.subText,
            fontSize: 11,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {dateLine}
        </Typography>
      </Box>
      <Typography
        sx={{
          color: leavePalette.subText,
          fontSize: 11.5,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {formatHistoryDays(row.business_days)}
      </Typography>
      <Box
        sx={{
          px: 1,
          py: 0.35,
          minWidth: 56,
          textAlign: "center",
          borderRadius: 5,
          fontSize: 10.5,
          fontWeight: 800,
          color,
          bgcolor: `${color}16`,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {row.status || "-"}
      </Box>
    </Stack>
  );
};

const LeaveDetailsBody = ({ onApplyLeave, yearState, tabState }) => {
  const {
    userDetails,
    selectedYear,
    setSelectedYear,
    leaveCount,
    leaveTypes,
    loading,
    leaveRequests,
    totals,
    expiry,
    recentHistory,
    yearOptions,
    selectedYearLabel,
    displayName,
  } = useLeaveDetailsData(yearState);

  const [tab, setTab] = tabState;
  const [loadPermissionApi, setLoadPersmissionApi] = useState(true);
  const [totalPermission, setTotalPermission] = useState(0);

  useEffect(() => {
    if (selectedYear) setLoadPersmissionApi(true);
  }, [selectedYear]);

  if (loading && !leaveCount) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 360,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader info="Loading..." />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 2.5, bgcolor: leavePalette.card, minHeight: "100%" }}>
      <ProfileCard
        leaveCount={leaveCount}
        userDetails={userDetails}
        displayName={displayName}
      />

      <YearSelector
        selectedYear={selectedYear}
        selectedYearLabel={selectedYearLabel}
        setSelectedYear={setSelectedYear}
        setTab={setTab}
        yearOptions={yearOptions}
      />

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          mb: 1.8,
          minHeight: 40,
          borderBottom: `1px solid ${leavePalette.border}`,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12,
            minHeight: 40,
            color: leavePalette.subText,
          },
          "& .Mui-selected": { color: `${leavePalette.green} !important` },
          "& .MuiTabs-indicator": {
            backgroundColor: leavePalette.green,
            height: 2,
          },
        }}
      >
        <Tab label="Summary" />
        <Tab label="Permissions" />
        <Tab label="History" />
      </Tabs>

      <Box
        sx={{
          border: `1px solid ${leavePalette.border}`,
          borderRadius: 2,
          p: 2,
          bgcolor: leavePalette.card,
        }}
      >
      {tab === 0 && (
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 1.15 }}
          >
            <Typography
              sx={{
                color: leavePalette.text,
                fontSize: 14.5,
                fontWeight: 800,
              }}
            >
              Leave Balances
            </Typography>
            <Typography sx={{ color: leavePalette.subText, fontSize: 11 }}>
              (In Days)
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.25,
            }}
          >
            {leaveTypes.length === 0 ? (
              <Box sx={{ ...panelCardSx, p: 1.5, gridColumn: "1 / -1" }}>
                <Typography sx={{ color: leavePalette.subText, fontSize: 12.5 }}>
                  No leave types configured.
                </Typography>
              </Box>
            ) : (
              leaveTypes.map((leaveType, index) => (
                <BalanceCard
                  key={leaveType.leave_type || index}
                  leaveType={leaveType}
                  index={index}
                />
              ))
            )}
          </Box>

          <Box sx={{ ...panelCardSx, mt: 1.7, px: 1.3 }}>
            <StatRow
              icon={EventAvailableIcon}
              color={leavePalette.green}
              label="Total Available"
              value={formatSummaryDays(totals.available)}
            />
            <StatRow
              icon={AccessTimeIcon}
              color={leavePalette.amber}
              label="Expiring On"
              value={expiry.label}
              sub={expiry.daysLeft !== null ? `(${expiry.daysLeft} days left)` : null}
            />
            <StatRow
              icon={BeachAccessIcon}
              color={leavePalette.blue}
              label="Leave Taken This Year"
              value={formatSummaryDays(totals.used)}
            />
            <StatRow
              icon={FlightTakeoffIcon}
              color={leavePalette.blue}
              label="Upcoming Leave"
              value={formatSummaryDays(totals.upcoming)}
              valueColor={leavePalette.blue}
            />
            <Button
              variant="outlined"
              fullWidth
              endIcon={<NavigateNextIcon sx={{ fontSize: 16 }} />}
              onClick={() => setTab(2)}
              sx={{
                my: 1,
                minHeight: 34,
                textTransform: "none",
                borderRadius: 1.5,
                borderColor: leavePalette.border,
                color: leavePalette.text,
                fontSize: 12,
                fontWeight: 800,
                "&:hover": {
                  borderColor: leavePalette.green,
                  color: leavePalette.green,
                },
              }}
            >
              View Full Year Summary
            </Button>
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2.5, mb: 1 }}
          >
            <Typography
              sx={{
                color: leavePalette.text,
                fontSize: 14.5,
                fontWeight: 800,
              }}
            >
              Recent Leave History
            </Typography>
            <Typography
              onClick={() => setTab(2)}
              sx={{
                color: leavePalette.blue,
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              View all
            </Typography>
          </Stack>

          {recentHistory.length === 0 ? (
            <Box sx={{ ...panelCardSx, p: 1.5 }}>
              <Typography sx={{ color: leavePalette.subText, fontSize: 12.5 }}>
                No leave requests found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ ...panelCardSx, px: 1.2 }}>
              {recentHistory.map((row, index) => (
                <RecentHistoryRow
                  key={row.id ?? index}
                  row={row}
                  isLast={index === recentHistory.length - 1}
                />
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={onApplyLeave}
            sx={{
              mt: 1.65,
              minHeight: 38,
              textTransform: "none",
              borderRadius: 1.5,
              bgcolor: leavePalette.green,
              boxShadow: "0 8px 18px rgba(18, 183, 106, 0.22)",
              fontWeight: 800,
              "&:hover": { bgcolor: "#0EA05B" },
            }}
          >
            Apply Leave
          </Button>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <PermissionsDetails
            selectedUser={{ user_id: userDetails?.id }}
            loadPermissionApi={loadPermissionApi}
            setLoadPersmissionApi={setLoadPersmissionApi}
            setTotalPermission={setTotalPermission}
            taxYearStartDateUser={toApiDate(selectedYear?.start_date)}
            taxYearEndDateUser={toApiDate(selectedYear?.end_date)}
          />
          <Typography
            sx={{
              color: leavePalette.subText,
              pt: 1,
              display: "block",
              fontSize: 12,
            }}
          >
            {totalPermission} permission(s) taken this year
          </Typography>
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ overflowX: "auto" }}>
          <LeaveHistoryTable rows={leaveRequests} onRowClick={() => {}} compact />
        </Box>
      )}
      </Box>
    </Box>
  );
};

export const LeaveDetailsDrawer = ({ open, onClose }) => {
  const history = useHistory();
  // Held here (above GlobalSidePanel) rather than inside the body: expanding
  // the panel remounts its children in a full-page portal, which would
  // otherwise reset the chosen leave year and tab back to their defaults.
  const yearState = useState(null);
  const tabState = useState(0);
  const resetYear = yearState[1];
  const resetTab = tabState[1];
  useEffect(() => {
    if (!open) {
      resetYear(null);
      resetTab(0);
    }
  }, [open, resetYear, resetTab]);

  const handleApplyLeave = () => {
    onClose();
    history.push("/admin/myzone/apply?view=leave&apply=true");
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Leave Details"
      subtitle="Individual leave summary and history"
      headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
    >
      {open ? (
        <LeaveDetailsBody
          onApplyLeave={handleApplyLeave}
          yearState={yearState}
          tabState={tabState}
        />
      ) : null}
    </GlobalSidePanel>
  );
};
